import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import Footer from "@/components/Footer";
import { api, formatINR } from "@/lib/api";
import { CheckCircle2, MapPin, Clock, Phone, IndianRupee, ArrowRight, MessageCircle, Share2, Copy, Gift } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SLOT_LABELS = {
  morning: "Morning (10 AM – 1 PM)",
  afternoon: "Afternoon (1 PM – 5 PM)",
  evening: "Evening (5 PM – 9 PM)",
};

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [config, setConfig] = useState({ business_whatsapp: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/orders/${orderId}`).then(({ data }) => setOrder(data)),
      api.get("/config").then(({ data }) => setConfig(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading order…</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center">Order not found</div>;

  const slotLabel = SLOT_LABELS[order.pickup_slot] || order.pickup_slot;
  const businessMsg = encodeURIComponent(
    `Hi Tech Value! 👋\n\nPlease confirm my pickup:\n\nRef: ${order.ref}\nDevice: ${order.product_name} · ${order.storage}\nOffer: ${formatINR(order.final_price)}\nPickup: ${order.pickup_date} · ${slotLabel}\nName: ${order.customer_name}\nPhone: ${order.customer_phone}\nAddress: ${order.address_line1}${order.address_line2 ? ", " + order.address_line2 : ""}, ${order.city} - ${order.pincode}\n\nThank you!`
  );
  const businessWA = config.business_whatsapp
    ? `https://wa.me/${config.business_whatsapp}?text=${businessMsg}`
    : null;

  const shareOrigin = window.location.origin;
  const referralShareText = encodeURIComponent(
    `Sell your old iPhone or Samsung on Tech Value for guaranteed spot cash 💰\n\nUse my referral code *${order.referral_code}* while booking — you get top price and I earn ₹200 when your pickup completes.\n\n${shareOrigin}`
  );
  const referralShareWA = `https://wa.me/?text=${referralShareText}`;

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(order.referral_code);
      toast.success("Referral code copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="order-confirmation-page">
      <HeaderNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4 tv-pulse">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Pickup Confirmed!</h1>
          <p className="text-muted-foreground mt-2">
            Your booking reference is <span className="font-mono font-bold text-foreground">{order.ref}</span>
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Device</div>
                <div className="text-xl font-semibold mt-1">{order.product_name}</div>
                <div className="text-sm text-muted-foreground">{order.storage}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payout</div>
                <div className="mt-1 flex items-end gap-0.5 justify-end text-accent">
                  <IndianRupee className="w-6 h-6" />
                  <span className="text-3xl font-extrabold font-mono">{order.final_price.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex gap-2">
                <Clock className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Pickup</div>
                  <div className="font-medium">{order.pickup_date}</div>
                  <div className="text-muted-foreground">{slotLabel}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Address</div>
                  <div className="font-medium leading-relaxed">
                    {order.address_line1}
                    {order.address_line2 && `, ${order.address_line2}`}
                    <br />
                    {order.city} – {order.pincode}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Customer</div>
                  <div className="font-medium">{order.customer_name}</div>
                  <div className="text-muted-foreground">{order.customer_phone}</div>
                </div>
              </div>
            </div>

            {businessWA && (
              <a href={businessWA} target="_blank" rel="noreferrer" className="mt-6 block" data-testid="whatsapp-confirm-link">
                <Button className="w-full h-11 bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2">
                  <MessageCircle className="w-5 h-5" /> Send WhatsApp Confirmation to Tech Value
                </Button>
              </a>
            )}
          </CardContent>
        </Card>

        {/* Referral card */}
        <Card className="mb-6 border-primary/30 overflow-hidden">
          <div className="bg-gradient-to-r from-primary to-sky-500 p-5 text-white">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-90">
              <Gift className="w-4 h-4" /> Your Referral Code
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono text-2xl sm:text-3xl font-extrabold tracking-widest" data-testid="my-referral-code">
                {order.referral_code}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="bg-white text-primary hover:bg-white/90 border-0" onClick={copyReferral} data-testid="copy-referral-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
                <a href={referralShareWA} target="_blank" rel="noreferrer" data-testid="share-referral-link">
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#20BD5A] text-white">
                    <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                  </Button>
                </a>
              </div>
            </div>
          </div>
          <CardContent className="p-5 text-sm">
            <div className="font-semibold mb-1">Earn ₹200 for every friend who sells</div>
            <p className="text-muted-foreground">
              Share this code with a friend. When they book a pickup with your code and their order is
              marked <span className="font-semibold text-foreground">Completed</span>, you earn <span className="font-semibold text-accent">₹200</span>.
              Track your credits anytime — payouts happen via UPI within 24 hours of the order being marked completed. <Link to={`/credits?phone=${encodeURIComponent(order.customer_phone)}`} className="text-primary font-semibold hover:underline">Check your credits →</Link>
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6 border-accent/30 bg-accent/5">
          <CardContent className="p-5 text-sm">
            <div className="font-semibold text-accent mb-1">💵 Cash on Pickup</div>
            <p className="text-muted-foreground">
              Our agent will inspect your device on-doorstep (3-min diagnostic). If it matches your answers,
              you receive the full {formatINR(order.final_price)} instantly via Cash or UPI. Zero online advance required.
            </p>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Link to="/"><Button variant="outline" data-testid="back-home-btn">Back to Home <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
